import React, { useState, useRef, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import "../styles/draftpaper.css";
import { FaBell, FaUserCircle, FaEdit, FaSearch, FaTrash } from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const Draftpaper = () => {
  const navigate = useNavigate();
  const [activeItem, setActiveItem] = useState("/papers/draftpaper");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [board, setBoard] = useState("");
  const [grade, setGrade] = useState("");
  const [subject, setSubject] = useState("");
  const [date, setDate] = useState("");
  const [drafts, setDrafts] = useState([]);
  const [selectedDrafts, setSelectedDrafts] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [boards, setBoards] = useState([]);
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);

  const [dashboardData, setDashboardData] = useState({
    totalBoard: 0,
    totalGrade: 0,
    totalSubjects: 0,
    totalPapers: 0,
    totalQuestions: 0,
  });

  const [user, setUser] = useState({
    id: null,
    name: "",
    email: "",
    password: "",
  });

  // ---------- Helper to convert dd-MM-yyyy -> yyyy-MM-dd ----------
  const formatDateForInput = (dateStr) => {
    if (!dateStr) return "";
    const parts = dateStr.split("-");
    if (parts.length !== 3) return dateStr;
    const [day, month, year] = parts;
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  };

  // ----- Fetch dashboard & user data -----
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get("https://digital.tifleducation.com/admin/public/api/getDashboard", {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.data) {
          setDashboardData({
            totalBoard: res.data.totalBoards || 0,
            totalGrade: res.data.totalGrades || 0,
            totalSubjects: res.data.totalSubjects || 0,
            totalPapers: res.data.totalPapers || 0,
            totalQuestions: res.data.totalQuestions || 0,
          });
          if (res.data.user) {
            setUser({
              id: res.data.user.id,
              name: res.data.user.name || "",
              email: res.data.user.email || "",
              password: "",
            });
          }
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error.response?.data || error);
      }
    };
    fetchDashboardData();
  }, []);

  // ----- Fetch draft papers -----
  const fetchDraftPapers = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        "https://digital.tifleducation.com/admin/public/api/papers/drafts",
        {
          status: 1,
          board: board || null,
          grade: grade || null,
          subject: subject || null,
          date: date || null,
          search: searchQuery || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data?.data?.data) {
        const formattedDrafts = res.data.data.data.map((p) => {
          if (!p.date) return p;
          const [day, month, year] = p.date.split("-");
          return { ...p, date: `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}` };
        });
        setDrafts(formattedDrafts);

        if (res.data.filters) {
          setBoards(res.data.filters.boards || []);
          setGrades(res.data.filters.grades || []);
          setSubjects(res.data.filters.subjects || []);
        }
      }
    } catch (error) {
      console.error("Error fetching draft papers:", error.response?.data || error);
      toast.error("Failed to load draft papers");
    }
  };

  useEffect(() => {
    fetchDraftPapers();
  }, [board, grade, subject, date, searchQuery]);

  // ----- Profile Save -----
  const handleSaveProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        "https://digital.tifleducation.com/admin/public/api/update-profile",
        { name: user.name, password: user.password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Profile updated successfully!");
      setIsModalOpen(false);
      setUser({ ...user, password: "" });
    } catch (error) {
      console.error("Error updating profile:", error.response?.data || error);
      toast.error("Failed to update profile");
    }
  };

  // ----- Dropdown toggle -----
  const toggleDropdown = () => setShowDropdown((prev) => !prev);

  // ----- Select All -----
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedDrafts(drafts.map((d) => d.id));
    } else {
      setSelectedDrafts([]);
    }
  };

  // ----- Single Checkbox -----
  const handleCheckboxChange = (id) => {
    setSelectedDrafts((prev) =>
      prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]
    );
  };

  // ----- Delete -----
  const handleDelete = async () => {
    if (selectedDrafts.length === 0) {
      toast.warning("Please select at least one draft to delete");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await Promise.all(
        selectedDrafts.map((id) =>
          axios.delete(`https://digital.tifleducation.com/admin/public/api/delete-papers/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        )
      );

      setDrafts((prev) => prev.filter((d) => !selectedDrafts.includes(d.id)));
      setSelectedDrafts([]);
      setIsDeleteModalOpen(false);

      toast.success("Selected draft(s) deleted successfully!");
    } catch (error) {
      console.error("Error deleting draft papers:", error.response?.data || error);
      toast.error("Failed to delete draft papers");
    }
  };

  // ----- Navigate to Preview -----
  const handleEdit = (paper) => {
    navigate(`/previewedit/${paper.id}`, {
      state: {
        paperId: paper.id,
        paperName: paper.paper_name,
        date: paper.date,
        duration: paper.duration,
        totalMarks: paper.total_marks,
        typeOfPaper: paper.type_of_paper,
        selected_questions: paper.selected_questions,
        boardId: paper.board,
        gradeId: paper.grade,
        subjectId: paper.subject,
      },
    });
  };

  // ----- Close dropdown on outside click -----
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => document.removeEventListener("click", handleClickOutside, true);
  }, []);

  return (
    <div className="dashboard-container">
      <Sidebar
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        activeItem={activeItem}
        setActiveItem={setActiveItem}
      />

      <main className="main-content">
        {/* Header */}
        <div className="draft-header">
          <div className="draft-welcome-text">
            <a href="/dashboard" className="back-link">
              <i className="fa fa-arrow-left" aria-hidden="true"></i>
            </a>
            <span className="draft-welcome-text">Draft Papers</span>
          </div>

          <div className="header-icons" ref={dropdownRef}>
            <FaBell className="icon" />
            <div className="user-dropdown-wrapper">
              <FaUserCircle className="icon" onClick={toggleDropdown} />
              {showDropdown && (
                <div className="user-dropdown">
                  <div className="edituser" onClick={() => setIsModalOpen(true)}>
                    <FaUserCircle className="ditusericon" />
                    <span>Edit Profile</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Dropdown Filters */}
        <div className="draft-dropdown-row">
          <div className="draft-dropdown-group">
            <label>Board</label>
            <select value={board} onChange={(e) => setBoard(e.target.value)}>
              <option value="">Select Board</option>
              {boards.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>

          <div className="draft-dropdown-group">
            <label>Grade</label>
            <select value={grade} onChange={(e) => setGrade(e.target.value)}>
              <option value="">Select Grade</option>
              {grades.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </div>

          <div className="draft-dropdown-group">
            <label>Date</label>
            <input
              type="date"
              className="draft-input-field"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>

          <div className="draft-search-bar-container">
            <div className="search-input-wrapper">
              <FaSearch className="search-icon" />
              <input
                type="text"
                className="search-input"
                placeholder="Search paper..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Draft List Header */}
        <div className="draft-list-header">
          <div className="left">
            <input
              type="checkbox"
              checked={selectedDrafts.length === drafts.length && drafts.length > 0}
              onChange={handleSelectAll}
            />
            <span className="select-all-label">Select All</span>
          </div>
          {selectedDrafts.length > 0 && (
            <button className="delete-btn" onClick={() => setIsDeleteModalOpen(true)}>
              <FaTrash /> Delete
            </button>
          )}
        </div>

        {/* Draft List */}
        <div className="draft-list">
          {drafts.length > 0 ? (
            drafts.map((paper) => (
              <div key={paper.id} className="draft-item">
                <div className="left">
                  <input
                    type="checkbox"
                    checked={selectedDrafts.includes(paper.id)}
                    onChange={() => handleCheckboxChange(paper.id)}
                  />
                  <div className="draft-info">
                    <span className="draft-name">{paper.paper_name}</span>
                    <span className="draft-date">{paper.date}</span>
                  </div>
                </div>
                <button className="draft-edit-btn" onClick={() => handleEdit(paper)}>
                  <FaEdit /> Edit
                </button>
              </div>
            ))
          ) : (
            <div className="no-data-message">
              <p>No draft papers found.</p>
              <p>
                {searchQuery || board || grade || subject || date
                  ? "Try changing your filters or search criteria."
                  : "Please create new draft papers to see them here."}
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Profile Edit Modal */}
      {isModalOpen && (
        <div style={modalStyles.overlay}>
          <div style={modalStyles.modal}>
            <button style={modalStyles.closeBtn} onClick={() => setIsModalOpen(false)}>
              &times;
            </button>
            <div style={modalStyles.userIcon}>
              <FaUserCircle />
            </div>

            <div style={modalStyles.formGroup}>
              <label>Username</label>
              <input
                type="text"
                value={user.name}
                onChange={(e) => setUser({ ...user, name: e.target.value })}
                style={modalStyles.input}
              />
            </div>

            <div style={modalStyles.formGroup}>
              <label>Email</label>
              <input
                type="email"
                value={user.email}
                disabled
                style={{ ...modalStyles.input, background: "#f0f0f0", color: "#888" }}
              />
            </div>

            <div style={modalStyles.formGroup}>
              <label>Password</label>
              <input
                type="password"
                value={user.password}
                onChange={(e) => setUser({ ...user, password: e.target.value })}
                style={modalStyles.input}
              />
            </div>

            <div style={{ textAlign: "right" }}>
              <button style={modalStyles.saveBtn} onClick={handleSaveProfile}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div style={modalStyles.overlay}>
          <div style={modalStyles.modal}>
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete the selected draft(s)?</p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "1rem" }}>
              <button
                style={{ ...modalStyles.saveBtn, background: "gray" }}
                onClick={() => setIsDeleteModalOpen(false)}
              >
                Cancel
              </button>
              <button style={modalStyles.saveBtn} onClick={handleDelete}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const modalStyles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "rgba(0,0,0,0.6)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  modal: {
    background: "#fff",
    padding: "2rem",
    borderRadius: "12px",
    width: "90%",
    maxWidth: "400px",
    position: "relative",
    boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
  },
  closeBtn: {
    position: "absolute",
    top: "10px",
    right: "15px",
    background: "none",
    border: "none",
    fontSize: "1.5rem",
    cursor: "pointer",
  },
  userIcon: {
    textAlign: "center",
    fontSize: "4rem",
    color: "#129255",
    marginBottom: "1rem",
  },
  formGroup: { marginBottom: "1rem" },
  input: {
    width: "100%",
    padding: "0.5rem 0.8rem",
    borderRadius: "6px",
    border: "1px solid #ccc",
    outline: "none",
  },
  saveBtn: {
    background: "#129255",
    color: "#fff",
    border: "none",
    padding: "0.6rem 1.2rem",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "bold",
  },
};

export default Draftpaper;
